import React from 'react';
import { Eye, Brain, Play, FileText } from 'lucide-react';

export const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: Eye,
      title: 'SEES',
      subtitle: 'Like a Human. Faster Than a Team.',
      description: 'Give it your APK. In minutes, it maps every screen, every user flow, every API call. On real physical devices, not emulators. It sees your app the way an attacker does.',
      status: 'ACTIVE',
    },
    {
      number: '02',
      icon: Brain,
      title: 'REASONS',
      subtitle: 'This Is What Scanners Will Never Do.',
      description: 'It doesn\'t match patterns. It thinks. It understands your authorization model. It figures out who should access what. Then it asks the dangerous questions.',
      example: [
        '"What if User A requests User B\'s payment history?"',
        '"What happens if I replay this token after logout?"',
        '"Can a free-tier user access premium endpoints?"',
      ],
      status: 'ACTIVE',
    },
    {
      number: '03',
      icon: Play,
      title: 'ATTACKS',
      subtitle: 'Real Attacks. Not Theoretical Risk.',
      description: 'It chains together multi-step exploits like a senior pentester would. Login, escalate, exfiltrate. If the vulnerability is real, it will find the path to prove it.',
      status: 'ACTIVE',
    },
    {
      number: '04',
      icon: FileText,
      title: 'PROVES',
      subtitle: 'Video Evidence. Not a PDF of Maybes.',
      description: 'Every finding comes with screen recordings, exact reproduction steps, API traces, and severity ratings. Hand it to your dev team. They can fix it today, not next quarter.',
      status: 'ACTIVE',
    },
  ];

  return (
    <section id="how-it-works" className="relative">
      <div className="section-divider"></div>

      <div className="container mx-auto px-6 py-28">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <div className="mb-20">
            <div className="tag mb-8">
              <span className="w-[6px] h-[6px] rounded-full bg-[#00ffd5] shadow-[0_0_8px_#00ffd5]"></span>
              How It Works
            </div>
            <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              We Gave AI
              <br />
              <span className="gradient-accent">A Hacker's Brain.</span>
            </h2>
            <p className="text-xl text-white/35 max-w-2xl" style={{ fontFamily: 'var(--font-body)' }}>
              Upload your app. Our AI does what a $50K pentest does.
              In minutes. On every build. Autonomously.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div
                key={index}
                className="card-tactical rounded-sm group"
              >
                <div className="flex items-stretch">
                  {/* Left number column */}
                  <div className="w-20 md:w-28 flex-shrink-0 flex flex-col items-center justify-center border-r border-white/[0.04] bg-white/[0.01]">
                    <span className="font-mono text-3xl md:text-4xl font-bold text-[#00ffd5]/20 group-hover:text-[#00ffd5]/50 transition-colors">
                      {step.number}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-7">
                    <div className="flex items-start gap-5">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#00ffd5]/[0.06] border border-[#00ffd5]/15 group-hover:bg-[#00ffd5]/[0.1] transition-colors">
                        <step.icon className="w-5 h-5 text-[#00ffd5]/70" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-mono text-lg font-bold text-white tracking-wider">
                            {step.title}
                          </h3>
                          <span className="font-mono text-[0.55rem] text-[#39ff14]/50 tracking-wider">
                            {step.status}
                          </span>
                        </div>
                        <p className="text-[#00ffd5]/60 text-sm font-mono mb-3">{step.subtitle}</p>
                        <p className="text-white/35 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                          {step.description}
                        </p>
                        {step.example && (
                          <div className="mt-4 bg-[#020204] border border-white/[0.04] p-4 rounded-sm">
                            <div className="space-y-2 font-mono text-xs">
                              {step.example.map((line, i) => (
                                <div key={i} className="flex items-start gap-3 text-white/40">
                                  <span className="text-[#00ffd5] mt-px">{'>'}</span>
                                  <span className="italic">{line}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
